import React, { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import type { VendingSalesFormValues } from '../../types/vendingSalesFormTypes';

type VendingSalesFormSyncProps = {
  control: any;
  getValues: () => VendingSalesFormValues;
  onFormDataUpdate: (data: VendingSalesFormValues) => void;
  delayMs?: number;
};

const VendingSalesFormSync: React.FC<VendingSalesFormSyncProps> = React.memo(({
  control,
  getValues,
  onFormDataUpdate,
  delayMs = 120,
}) => {
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousLineCountRef = useRef<number | null>(null);
  const watchedAction = useWatch({ control, name: 'action' });
  const watchedLines = useWatch({ control, name: 'parsed_data.final_json.lines' });
  const watchedSummary = useWatch({ control, name: 'parsed_data.final_json.summary' });
  const watchedLineCount = Array.isArray(watchedLines) ? watchedLines.length : 0;

  useEffect(() => {
    if (!onFormDataUpdate) {
      return;
    }

    const previousLineCount = previousLineCountRef.current;
    previousLineCountRef.current = watchedLineCount;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    if (previousLineCount !== null && previousLineCount !== watchedLineCount) {
      const currentFormData = getValues();
      onFormDataUpdate({
        ...currentFormData,
        action: watchedAction,
        parsed_data: {
          ...(currentFormData.parsed_data ?? {}),
          final_json: {
            ...(currentFormData.parsed_data?.final_json ?? {}),
            summary: watchedSummary,
            lines: watchedLines,
          },
        },
      });

      return;
    }

    syncTimeoutRef.current = setTimeout(() => {
      const currentFormData = getValues();
      onFormDataUpdate({
        ...currentFormData,
        action: watchedAction,
        parsed_data: {
          ...(currentFormData.parsed_data ?? {}),
          final_json: {
            ...(currentFormData.parsed_data?.final_json ?? {}),
            summary: watchedSummary,
            lines: watchedLines,
          },
        },
      });
    }, delayMs);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [delayMs, getValues, onFormDataUpdate, watchedAction, watchedLineCount, watchedLines, watchedSummary]);

  return null;
});

VendingSalesFormSync.displayName = 'VendingSalesFormSync';

export default VendingSalesFormSync;
